import { z } from "zod";

import { type CreateMaterialDto } from "#modules/materials/infra/createMaterial.api";
import { euroToCents, parseInputNumberValue } from "#shared/helpers/prices";
import {
  getMaterialDensity,
  getMaterialDimensionsFieldsConfiguration,
  MaterialGradeSchema,
  MaterialGradeType,
  MaterialTypeSchema,
  MaterialTypeType,
} from "shared";

import { isValidEuroAmount } from "./isValidEuroAmount";

const dimensionsSchema = z.object({
  thickness: z.number().optional(),
  diameter: z.number().optional(),
  height: z.number().optional(),
  width: z.number().optional(),
  length: z.number().optional(),
});

type RawMaterialDimensions = z.input<typeof dimensionsSchema>;

const buildDimensionsPayload = (
  type: MaterialTypeType | null,
  dimensions: RawMaterialDimensions,
) => {
  const payload: { height?: number; width?: number; length?: number; tubeThickness?: number } = {};
  getMaterialDimensionsFieldsConfiguration(type).forEach(({ field, key, syncWith }) => {
    const value = dimensions[key] ?? 0;
    payload[field] = value;
    if (syncWith) {
      payload[syncWith] = value;
    }
  });
  return payload;
};

export const AddRawMaterialFormSchema = z
  .object({
    // Kept nullable (not narrowed via .refine) rather than validated here: the
    // dimensions check below needs the raw type value, valid or not, to know
    // which dimension keys apply.
    type: MaterialTypeSchema.nullable(),
    materialGrade: MaterialGradeSchema.nullable().refine(
      (value): value is MaterialGradeType => value !== null,
    ),
    name: z.string().trim().min(1),
    dimensions: dimensionsSchema,
    price: z.string().refine(isValidEuroAmount),
  })
  .superRefine((values, ctx) => {
    if (!values.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["type"],
      });
    }
    const hasAllDimensions = getMaterialDimensionsFieldsConfiguration(values.type).every(
      ({ key }) => values.dimensions[key] !== undefined,
    );
    if (!hasAllDimensions) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dimensions"],
      });
    }
  })
  .transform((values): CreateMaterialDto => {
    const dimensionsPayload = buildDimensionsPayload(values.type, values.dimensions);

    return {
      name: values.name,
      type: values.type as MaterialTypeType,
      subType: null,
      materialGrade: values.materialGrade,
      height: dimensionsPayload.height ?? 0,
      width: dimensionsPayload.width ?? 0,
      length: dimensionsPayload.length ?? 0,
      tubeThickness: dimensionsPayload.tubeThickness ?? null,
      density: getMaterialDensity({ materialGrade: values.materialGrade }),
      price: euroToCents(parseInputNumberValue(values.price)),
    };
  });

export type AddRawMaterialFormValues = z.input<typeof AddRawMaterialFormSchema>;
